/**
 * PendingSalesFormsWidget
 * Shows pending sales forms for verification (manager/leader only)
 * Extracted from ManagerDashboard.js
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Button from '../../common/Button';
import Modal from '../../common/Modal';
import api from '../../../services/api';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

export default function PendingSalesFormsWidget() {
  const [pendingSalesForms, setPendingSalesForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [availableSids, setAvailableSids] = useState([]);
  const [selectedSidId, setSelectedSidId] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [projectNumberError, setProjectNumberError] = useState('');
  const [nextProjectNumber, setNextProjectNumber] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectingFormId, setRejectingFormId] = useState(null);
  const [isMinimized, setIsMinimized] = useState(() => {
    // Persist minimized state in localStorage
    const saved = localStorage.getItem('pendingSalesFormsMinimized');
    return saved === 'true';
  });

  // Mobile detection
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Save minimized state to localStorage
  const toggleMinimized = () => {
    setIsMinimized(prev => {
      const newState = !prev;
      localStorage.setItem('pendingSalesFormsMinimized', String(newState));
      return newState;
    });
  };

  useEffect(() => {
    fetchPendingSalesForms();
    fetchAvailableSids();
  }, []);

  async function fetchPendingSalesForms() {
    try {
      setFormsLoading(true);
      const response = await api.get('/sales-forms?status=pending_verification');
      setPendingSalesForms(response.data.salesForms || []);
    } catch (error) {
      console.error('Fetch pending sales forms error:', error);
    } finally {
      setFormsLoading(false);
    }
  }

  async function fetchAvailableSids() {
    try {
      const [sidsResponse, asidsResponse] = await Promise.all([
        api.get('/users?role=sid'),
        api.get('/users?role=asid')
      ]);

      const allSids = [
        ...(sidsResponse.data.users || []),
        ...(asidsResponse.data.users || [])
      ];

      const activeSids = allSids
        .filter(user => !user.isResigned)
        .sort((a, b) => {
          if (a.role === 'sid' && b.role === 'asid') return -1;
          if (a.role === 'asid' && b.role === 'sid') return 1;
          return 0;
        });

      setAvailableSids(activeSids);
    } catch (error) {
      console.error('Fetch SIDs error:', error);
      toast.error('Failed to load available SIDs');
    }
  }

  async function handleViewDetails(salesFormId) {
    try {
      // Fetch form details and next project number in parallel
      const [formResponse, numberResponse] = await Promise.all([
        api.get(`/sales-forms/${salesFormId}`),
        api.get('/design-projects/next-project-number')
      ]);
      setSelectedForm(formResponse.data.salesForm);
      setSelectedSidId('');
      setProjectNumber('');
      setProjectNumberError('');
      setNextProjectNumber(numberResponse.data.nextProjectNumber || '');
      setShowDetailModal(true);
    } catch (error) {
      console.error('Fetch sales form details error:', error);
      toast.error('Failed to load sales form details');
    }
  }

  async function handleVerifySalesForm(salesFormId) {
    // Validate project number format if provided
    if (projectNumber && !/^\d{4}$/.test(projectNumber)) {
      setProjectNumberError('Project number must be exactly 4 digits');
      return;
    }
    setProjectNumberError('');

    try {
      const payload = {};
      if (selectedSidId) {
        payload.sidId = selectedSidId;
      }
      if (projectNumber) {
        payload.projectNumber = projectNumber;
      }

      await api.patch(`/sales-forms/${salesFormId}/verify`, payload);

      if (selectedSidId) {
        toast.success('Sales form verified and SID assigned! Project created successfully.');
      } else {
        toast.success('Sales form verified! Project created. Remember to assign a SID.');
      }

      setShowDetailModal(false);
      setSelectedForm(null);
      setSelectedSidId('');
      setProjectNumber('');
      fetchPendingSalesForms();
    } catch (error) {
      console.error('Verify sales form error:', error);
      toast.error(error.response?.data?.error || 'Failed to verify sales form');
    }
  }

  function openRejectModal(salesFormId) {
    setRejectingFormId(salesFormId);
    setRejectionReason('');
    setShowRejectModal(true);
  }

  async function handleRejectSalesForm() {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await api.patch(`/sales-forms/${rejectingFormId}/reject`, {
        reason: rejectionReason
      });
      toast.success('Sales form rejected');
      setShowDetailModal(false);
      setSelectedForm(null);
      setShowRejectModal(false);
      setRejectionReason('');
      setRejectingFormId(null);
      fetchPendingSalesForms();
    } catch (error) {
      console.error('Reject sales form error:', error);
      toast.error(error.response?.data?.error || 'Failed to reject sales form');
    }
  }

  // Don't render anything if not loading and no pending forms
  if (!formsLoading && pendingSalesForms.length === 0) {
    return null;
  }

  return (
    <>
      <div className={isMobile ? "mb-4" : "card mb-8"}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className={`font-display font-bold ${isMobile ? 'text-base' : 'text-xl'}`}>Pending Sales Forms</h2>
            <span className={`inline-flex items-center rounded-full font-medium bg-yellow-100 text-yellow-800 ${isMobile ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs'}`}>
              {pendingSalesForms.length}
            </span>
          </div>
          <button
            onClick={toggleMinimized}
            className="flex items-center gap-1 px-2 py-1 text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            title={isMinimized ? 'Expand section' : 'Minimize section'}
          >
            <svg className={`w-4 h-4 transition-transform ${isMinimized ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            {!isMobile && <span>{isMinimized ? 'Expand' : 'Minimize'}</span>}
          </button>
        </div>

        {!isMinimized && (
          <>
            {formsLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading...</p>
              </div>
            ) : isMobile ? (
              /* Mobile Horizontal Scroll View */
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                {pendingSalesForms.map((form) => (
                  <div
                    key={form.id}
                    onClick={() => handleViewDetails(form.id)}
                    className="flex-shrink-0 w-44 p-3 rounded-xl border border-gray-200 bg-white shadow-sm active:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <p className="text-sm font-bold text-amber-600 mb-1">
                      RM {parseFloat(form.estimatedPackagePrice).toLocaleString('en-MY', { minimumFractionDigits: 0 })}
                    </p>
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {form.clientName}
                    </h3>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {form.city || form.addressLine1 || '-'}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              /* Desktop Table View */
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent(s) (Leader(s))
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Package Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingSalesForms.map((form) => {
                  // Build agents and leaders display
                  const agentLeaderPairs = [];

                  // Submitting agent
                  const submittingAgentName = form.agent?.nickname || 'Unknown';
                  const submittingLeaderName = form.agent?.leader?.nickname || '-';
                  agentLeaderPairs.push({ agent: submittingAgentName, leader: submittingLeaderName });

                  // Co-closing agents
                  (form.coClosingAgentsResolved || []).forEach(ca => {
                    const coAgentName = ca.agent?.nickname || 'Unknown';
                    const coLeaderName = ca.salesLeader?.nickname || '-';
                    agentLeaderPairs.push({ agent: coAgentName, leader: coLeaderName });
                  });

                  // Group by leader
                  const leaderGroups = {};
                  agentLeaderPairs.forEach(pair => {
                    if (!leaderGroups[pair.leader]) {
                      leaderGroups[pair.leader] = [];
                    }
                    leaderGroups[pair.leader].push(pair.agent);
                  });

                  return (
                    <tr key={form.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {form.clientName}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {form.city || form.addressLine1 || '-'}
                        </div>
                        {form.state && form.city && (
                          <div className="text-sm text-gray-500">{form.state}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 space-y-1">
                          {Object.entries(leaderGroups).map(([leader, agents], idx) => (
                            <div key={idx}>
                              <span className="font-medium">{agents.join(', ')}</span>
                              <span className="text-gray-500"> ({leader})</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {form.propertyType?.replace(/_/g, ' ').toUpperCase()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        RM {parseFloat(form.estimatedPackagePrice).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(form.createdAt).toLocaleDateString('en-MY')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleViewDetails(form.id)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sales Form Detail Modal */}
      {selectedForm && (
        <Modal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedForm(null);
          }}
          title="Sales Form Review"
          size="large"
        >
          <div className="space-y-4 md:space-y-6">
            {/* Header - Client & Price */}
            {isMobile ? (
              /* Mobile Header */
              <div className="space-y-3">
                <div className="bg-amber-50 px-4 py-3 rounded-xl -mx-2">
                  <p className="text-xs text-amber-600 font-medium text-center">Package Price</p>
                  <p className="text-2xl font-bold text-amber-700 text-center">
                    RM {parseFloat(selectedForm.estimatedPackagePrice).toLocaleString('en-MY', { minimumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{selectedForm.clientName}</h2>
                  <div className="mt-1 space-y-0.5 text-sm text-gray-500">
                    <p>{selectedForm.clientPhone}</p>
                    <p>{selectedForm.clientIC}</p>
                    {selectedForm.clientEmail && <p>{selectedForm.clientEmail}</p>}
                  </div>
                </div>
              </div>
            ) : (
              /* Desktop Header */
              <div className="flex items-start justify-between pb-5 border-b border-gray-100">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedForm.clientName}</h2>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                    <span>{selectedForm.clientPhone}</span>
                    <span className="text-gray-300">•</span>
                    <span>{selectedForm.clientIC}</span>
                    {selectedForm.clientEmail && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span>{selectedForm.clientEmail}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right bg-amber-50 px-4 py-2 rounded-lg">
                  <p className="text-xs text-amber-600 font-medium">Package Price</p>
                  <p className="text-2xl font-bold text-amber-700">
                    RM {parseFloat(selectedForm.estimatedPackagePrice).toLocaleString('en-MY', { minimumFractionDigits: 0 })}
                  </p>
                </div>
              </div>
            )}

            {/* Content - Responsive Layout */}
            {isMobile ? (
              /* Mobile: Single Column, Reordered */
              <div className="space-y-4">
                {/* Deposit - Priority 1 */}
                <div className="bg-green-50 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xs font-medium text-green-600 uppercase tracking-wider mb-1">Deposit Received</h3>
                      <p className="text-xl font-bold text-green-700">
                        RM {parseFloat(selectedForm.depositAmount).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-green-600 mt-0.5">
                        {new Date(selectedForm.depositReceivedDate).toLocaleDateString('en-MY')}
                      </p>
                    </div>
                    {selectedForm.depositProofUrl && (
                      <a
                        href={selectedForm.depositProofUrl.startsWith('http') ? selectedForm.depositProofUrl : `${(process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '')}${selectedForm.depositProofUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-3 py-2 bg-white text-green-700 text-sm font-medium rounded-lg hover:bg-green-100 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Proof
                      </a>
                    )}
                  </div>
                </div>

                {/* Project Number & Assign SID - Priority 2 */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  {/* Project Number Input */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Project Number</h3>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={projectNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setProjectNumber(val);
                        setProjectNumberError('');
                      }}
                      placeholder="e.g., 0001 (leave empty for auto)"
                      maxLength={4}
                      className={`w-full px-3 py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${projectNumberError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
                    />
                    {projectNumberError && (
                      <p className="text-xs text-red-500 mt-1">{projectNumberError}</p>
                    )}
                    {nextProjectNumber && !projectNumber && (
                      <p className="text-[10px] text-green-600 mt-1">Auto: {nextProjectNumber}</p>
                    )}
                    {!nextProjectNumber && !projectNumber && (
                      <p className="text-[10px] text-gray-400 mt-1">Leave empty for auto-generation</p>
                    )}
                  </div>

                  {/* Assign Designer */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Assign Designer <span className="text-red-500">*</span></h3>
                    <select
                      value={selectedSidId}
                      onChange={(e) => setSelectedSidId(e.target.value)}
                      className={`w-full px-3 py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${!selectedSidId ? 'border-red-300 bg-white' : 'border-gray-200 bg-white'}`}
                    >
                      <option value="">-- Select SID/ASID --</option>
                      {(() => {
                        const sids = availableSids.filter(u => u.role === 'sid');
                        const asids = availableSids.filter(u => u.role === 'asid');
                        return sids.map(sid => (
                          <optgroup key={sid.id} label={sid.nickname || sid.fullName}>
                            <option value={sid.id}>{sid.nickname || sid.fullName} (SID)</option>
                            {asids.filter(asid => asid.sidId === sid.id).map(asid => (
                              <option key={asid.id} value={asid.id}>└ {asid.nickname || asid.fullName} (ASID)</option>
                            ))}
                          </optgroup>
                        ));
                      })()}
                    </select>
                  </div>
                </div>

                {/* Property & Sales Team Row */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Property */}
                  <div className="bg-blue-50 rounded-xl p-3">
                    <h3 className="text-[10px] font-medium text-blue-600 uppercase tracking-wider mb-1">Property</h3>
                    <p className="text-xs text-gray-900 line-clamp-2">
                      {selectedForm.city || selectedForm.addressLine1 || 'Not provided'}
                    </p>
                    <span className="inline-block mt-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
                      {selectedForm.propertyType?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>

                  {/* Sales Team */}
                  <div className="bg-purple-50 rounded-xl p-3">
                    <h3 className="text-[10px] font-medium text-purple-600 uppercase tracking-wider mb-1">Sales Team</h3>
                    <p className="text-xs font-medium text-gray-900">
                      {selectedForm.agent?.nickname || selectedForm.agent?.fullName}
                    </p>
                    {selectedForm.agent?.leader?.nickname && (
                      <p className="text-[10px] text-gray-500">Leader: {selectedForm.agent.leader.nickname}</p>
                    )}
                  </div>
                </div>

                {/* Budget - Compact */}
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Budget Range</span>
                  <span className="text-xs font-medium text-gray-900">
                    {selectedForm.budgetMin ? `RM ${parseFloat(selectedForm.budgetMin).toLocaleString('en-MY', { minimumFractionDigits: 0 })}` : '-'}
                    {' → '}
                    {selectedForm.budgetMax ? `RM ${parseFloat(selectedForm.budgetMax).toLocaleString('en-MY', { minimumFractionDigits: 0 })}` : '-'}
                  </span>
                </div>
              </div>
            ) : (
              /* Desktop: Two Column Layout */
              <div className="grid grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-5">
                  {/* Property */}
                  <div className="pl-3 border-l-2 border-blue-200">
                    <h3 className="text-xs font-medium text-blue-600 uppercase tracking-wider mb-2">Property</h3>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-900">
                        {selectedForm.addressLine1 ? (
                          <>
                            {selectedForm.addressLine1}
                            {selectedForm.addressLine2 && `, ${selectedForm.addressLine2}`}
                            <br />
                            <span className="text-gray-500">{selectedForm.postcode} {selectedForm.city}{selectedForm.state && `, ${selectedForm.state}`}</span>
                          </>
                        ) : (
                          selectedForm.propertyAddress || 'Not provided'
                        )}
                      </p>
                      <div className="flex items-center gap-3 text-sm pt-1">
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                          {selectedForm.propertyType?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        {selectedForm.propertySize && (
                          <span className="text-gray-500">{selectedForm.propertySize} sq ft</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="pl-3 border-l-2 border-gray-200">
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Budget Range</h3>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-900 font-medium">
                        {selectedForm.budgetMin ? `RM ${parseFloat(selectedForm.budgetMin).toLocaleString('en-MY', { minimumFractionDigits: 0 })}` : '-'}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="text-gray-900 font-medium">
                        {selectedForm.budgetMax ? `RM ${parseFloat(selectedForm.budgetMax).toLocaleString('en-MY', { minimumFractionDigits: 0 })}` : '-'}
                      </span>
                    </div>
                  </div>

                  {/* Project Scope */}
                  <div className="pl-3 border-l-2 border-gray-200">
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Project Details</h3>
                    <div className="space-y-1.5 text-sm">
                      <p className="text-gray-900">{selectedForm.projectScope || 'Not specified'}</p>
                      {selectedForm.specialRequirements && (
                        <p className="text-gray-500 text-xs">Requirements: {selectedForm.specialRequirements}</p>
                      )}
                      {selectedForm.preferredStartDate && (
                        <p className="text-gray-500 text-xs">Start: {new Date(selectedForm.preferredStartDate).toLocaleDateString('en-MY')}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-5">
                  {/* Deposit */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xs font-medium text-green-600 uppercase tracking-wider mb-1">Deposit Received</h3>
                        <p className="text-xl font-bold text-green-700">
                          RM {parseFloat(selectedForm.depositAmount).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-green-600 mt-0.5">
                          {new Date(selectedForm.depositReceivedDate).toLocaleDateString('en-MY')}
                        </p>
                      </div>
                      {selectedForm.depositProofUrl && (
                        <a
                          href={selectedForm.depositProofUrl.startsWith('http') ? selectedForm.depositProofUrl : `${(process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '')}${selectedForm.depositProofUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 bg-white text-green-700 text-sm font-medium rounded-md hover:bg-green-100 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Proof
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Sales Team */}
                  <div className="pl-3 border-l-2 border-purple-200">
                    <h3 className="text-xs font-medium text-purple-600 uppercase tracking-wider mb-3">Sales Team</h3>
                    <div className="space-y-2">
                      {/* Submitting Agent */}
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedForm.agent?.nickname || selectedForm.agent?.fullName}
                          </span>
                          {selectedForm.agent?.leader?.nickname && (
                            <span className="text-xs text-gray-400 ml-1">
                              ({selectedForm.agent.leader.nickname})
                            </span>
                          )}
                        </div>
                        {selectedForm.primaryCloserId === selectedForm.agentId && (
                          <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded font-medium">Primary</span>
                        )}
                      </div>

                      {/* Co-Closing Agents */}
                      {selectedForm.coClosingAgentsResolved && selectedForm.coClosingAgentsResolved.map((coAgent, index) => (
                        <div key={coAgent.agentId || index} className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              {coAgent.agent?.nickname || coAgent.agent?.fullName || 'Unknown'}
                            </span>
                            {coAgent.salesLeader?.nickname && (
                              <span className="text-xs text-gray-400 ml-1">
                                ({coAgent.salesLeader.nickname})
                              </span>
                            )}
                          </div>
                          {selectedForm.primaryCloserId === coAgent.agentId && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded font-medium">Primary</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Project Number */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Project Number</h3>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={projectNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setProjectNumber(val);
                        setProjectNumberError('');
                      }}
                      placeholder="e.g., 0001 (leave empty for auto)"
                      maxLength={4}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 ${projectNumberError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                    />
                    {projectNumberError && (
                      <p className="text-xs text-red-500 mt-1">{projectNumberError}</p>
                    )}
                    {nextProjectNumber && !projectNumber && (
                      <p className="text-xs text-green-600 mt-1">Auto: {nextProjectNumber}</p>
                    )}
                    {!nextProjectNumber && !projectNumber && (
                      <p className="text-xs text-gray-400 mt-1">Leave empty for auto-generation</p>
                    )}
                  </div>

                  {/* Assign SID */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Assign Designer <span className="text-red-500">*</span></h3>
                    <select
                      value={selectedSidId}
                      onChange={(e) => setSelectedSidId(e.target.value)}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 ${!selectedSidId ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                    >
                      <option value="">-- Select SID/ASID --</option>
                      {(() => {
                        const sids = availableSids.filter(u => u.role === 'sid');
                        const asids = availableSids.filter(u => u.role === 'asid');

                        return sids.map(sid => (
                          <optgroup key={sid.id} label={sid.nickname || sid.fullName}>
                            <option value={sid.id}>
                              {sid.nickname || sid.fullName} (SID)
                            </option>
                            {asids
                              .filter(asid => asid.sidId === sid.id)
                              .map(asid => (
                                <option key={asid.id} value={asid.id}>
                                  └ {asid.nickname || asid.fullName} (ASID)
                                </option>
                              ))
                            }
                          </optgroup>
                        ));
                      })()}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            {isMobile ? (
              /* Mobile Footer - Stacked Buttons */
              <div className="pt-4 mt-2 border-t border-gray-100 space-y-3">
                <button
                  onClick={() => handleVerifySalesForm(selectedForm.id)}
                  disabled={!selectedSidId}
                  className={`w-full flex items-center justify-center gap-2 px-5 py-3 text-white font-semibold rounded-xl transition-all shadow-md ${!selectedSidId ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 active:scale-[0.98]'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Verify & Assign SID
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => openRejectModal(selectedForm.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-red-50 active:bg-red-100 text-red-600 text-sm font-medium rounded-xl border border-red-200 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedForm(null);
                      setSelectedSidId('');
                      setProjectNumber('');
                    }}
                    className="flex-1 px-4 py-2.5 bg-gray-100 active:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl border border-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 text-center">
                  Submitted {new Date(selectedForm.createdAt).toLocaleDateString('en-MY')}
                </p>
              </div>
            ) : (
              /* Desktop Footer */
              <div className="pt-5 mt-2 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    Submitted {new Date(selectedForm.createdAt).toLocaleDateString('en-MY')} at {new Date(selectedForm.createdAt).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openRejectModal(selectedForm.id)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-lg border border-red-200 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Reject
                    </button>
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        setSelectedForm(null);
                        setSelectedSidId('');
                        setProjectNumber('');
                      }}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg border border-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleVerifySalesForm(selectedForm.id)}
                      disabled={!selectedSidId}
                      className={`inline-flex items-center gap-2 px-5 py-2 text-white text-sm font-medium rounded-lg transition-colors shadow-sm ${!selectedSidId ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Verify & Assign SID
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <Modal
          isOpen={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setRejectionReason('');
            setRejectingFormId(null);
          }}
          title="Reject Sales Form"
          size="medium"
        >
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <svg className="h-5 w-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">Warning</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    This action will reject the sales form and notify the agent. Please provide a clear reason for rejection.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="label">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="input w-full"
                rows="4"
                placeholder="E.g., Deposit proof is unclear, please resubmit with a clearer image..."
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                The agent will see this reason and can resubmit the form after corrections.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setRejectingFormId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleRejectSalesForm}
                className="bg-red-600 hover:bg-red-700"
                disabled={!rejectionReason.trim()}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
